import { TinyMCEEditor } from '../types/shell';

const SCHEME = /^[a-z][a-z\d+.-]*:/i;
const HOST_WITH_PORT = /^(?:localhost|[^\s/?#@:]+\.[^\s/?#@:]+):\d{1,5}(?:[/?#]|$)/i;
const RELATIVE_REFERENCE = /^[#?/.]/;
const EMAIL_LIKE = /^[^/?#]*@/;
const SITE_HOST = /^(?:localhost(?=[:/?#]|$)|[^/?#\s]*\.)/i;

export const normalizeLinkHref = (href: string): string => {
  const url = href.trim();

  if (url.startsWith('//')) {
    return `https:${url}`;
  }

  if (!url || RELATIVE_REFERENCE.test(url) || EMAIL_LIKE.test(url)) {
    return url;
  }

  if (SCHEME.test(url) && !HOST_WITH_PORT.test(url)) {
    return url;
  }

  return SITE_HOST.test(url) ? `https://${url}` : url;
};

interface ILinkDialogData {
  url?: { value?: unknown; meta?: unknown };
}

interface ILinkDialogApi {
  getData: () => ILinkDialogData;
  setData: (data: ILinkDialogData) => void;
}

interface IDialogSpec {
  body?: { items?: unknown };
  onSubmit?: (api: ILinkDialogApi) => void;
}

type TOpenDialog = (spec: IDialogSpec, params?: unknown) => unknown;

const wrappedWindowManagers = new WeakSet<object>();

const isLinkDialog = (spec: IDialogSpec) => {
  const items = spec?.body?.items;

  return (
    Array.isArray(items) &&
    items.some((item: { type?: unknown; name?: unknown } | null) => item?.type === 'urlinput' && item?.name === 'url')
  );
};

const normalizeDialogUrl = (api: ILinkDialogApi) => {
  const { url } = api.getData();

  if (!url || typeof url.value !== 'string') {
    return;
  }

  const value = normalizeLinkHref(url.value);

  if (value !== url.value) {
    api.setData({ url: { ...url, value } });
  }
};

export const wrapLinkDialogSubmit = (editor: Pick<TinyMCEEditor, 'windowManager'>) => {
  const windowManager = editor.windowManager as unknown as { open: TOpenDialog };

  if (wrappedWindowManagers.has(windowManager)) {
    return;
  }

  wrappedWindowManagers.add(windowManager);
  const open = windowManager.open.bind(windowManager);

  windowManager.open = (spec, params) => {
    const { onSubmit } = spec ?? {};

    if (!onSubmit || !isLinkDialog(spec)) {
      return open(spec, params);
    }

    return open(
      {
        ...spec,
        onSubmit: (api) => {
          normalizeDialogUrl(api);
          onSubmit(api);
        },
      },
      params,
    );
  };
};
